// virtualWindow — pure windowing math shared by the dashboard's hand-rolled
// virtualizers (DataTable rows, StickyFolder's VirtualFolderList items).
//
// Lifted as a *reference reimplementation* (not a dependency) of the
// layout/virtualization split in @pierre/trees. Two ideas ported here:
//
//   1. Pure window computation. The geometry lives in plain functions with no
//      DOM access, so it is unit-testable in isolation (see virtualWindow.test.js)
//      and shared by every virtualized surface instead of re-derived per
//      component. The component keeps the single imperative scroll read + DOM
//      writes; this module only does arithmetic.
//
//   2. Window hysteresis. `reconcileWindow` returns the *previous* window
//      object (same reference) when the freshly-visible band still sits inside
//      the already-rendered overscan buffer. A scroll that uncovers no new rows
//      therefore yields no new object, and the caller can skip its state update
//      entirely — the difference between re-rendering the visible rows on every
//      scroll frame vs. only when the window actually moves.
//
// Unlike trees we keep a *measured* (variable-height) model: our rows wrap
// (trace context pills, installed-AI-agent lists, scope sparklines), so we
// can't use trees' O(1) `floor(scrollTop / itemHeight)` math. The scan is
// linear in row count per recompute — fine to ~10k rows; past that this is
// where a Fenwick/prefix-sum index would go (the long-standing DataTable TODO).
// Hysteresis is what keeps that scan off the hot path on most scroll frames.
//
// Coordinate model (all in scroll-content pixels):
//   intoRows = max(0, scrollTop - baselineTop)   // how far rows have scrolled
//                                                 // past the top of the viewport
//   visible band = [intoRows, intoRows + viewportH)
//   window       = visible band padded by overscanPx on each side
//
// `baselineTop` is the rows container's offset inside the scroll viewport
// (e.g. a sticky header's height); pass 0 when the container starts at the top.

// Stable empty window — `start === end` renders nothing, zero spacers.
export const EMPTY_WINDOW = Object.freeze({ start: 0, end: 0, topH: 0, bottomH: 0 });

// Value equality for two windows. Lets a caller that must recompute every
// render (e.g. because its row-id list is a fresh array each time) still avoid
// a state update — and the render loop that would follow — when the window
// didn't actually move.
export function windowsEqual(a, b) {
    return (
        a.start === b.start &&
        a.end === b.end &&
        a.topH === b.topH &&
        a.bottomH === b.bottomH
    );
}

// Resolve [start, end) for the content interval [intoRows - padTop,
// intoRows + viewportH + padBottom). With padTop/padBottom = 0 this is the bare
// visible band (used for the hysteresis containment test); with padTop/padBottom
// = overscanPx it is the rendered window. Mirrors the variable-height scan that
// VirtualFolderList already trusted, generalized over the padding.
function scanBand({ scrollTop, baselineTop = 0, viewportH, count, heightOf, padTop = 0, padBottom = 0 }) {
    const intoRows = Math.max(0, scrollTop - baselineTop);
    const top = intoRows - padTop;
    const bottom = intoRows + viewportH + padBottom;

    let accum = 0;
    let start = 0;
    let end = count;
    for (let i = 0; i < count; i++) {
        const h = heightOf(i);
        // Row i lies fully above the window top → advance start past it.
        if (accum + h < top) start = i + 1;
        // Row i starts below the window bottom → nothing further is visible.
        if (accum > bottom) {
            end = i;
            break;
        }
        accum += h;
    }
    if (start > end) start = end;
    return { start, end };
}

// Full rendered window: the visible band expanded by `overscanPx` on each side,
// plus the top/bottom spacer heights that keep the scrollbar faithful when only
// the slice is mounted. Returns the "render everything" window when the viewport
// is not yet measured (viewportH <= 0) or the list is empty, matching the
// no-virtualization fallback both callers already relied on.
export function computeWindow(metrics) {
    const { viewportH, count, heightOf, overscanPx = 0 } = metrics;
    if (viewportH <= 0 || count === 0) {
        return { start: 0, end: count, topH: 0, bottomH: 0 };
    }

    const { start, end } = scanBand({ ...metrics, padTop: overscanPx, padBottom: overscanPx });

    let topH = 0;
    for (let i = 0; i < start; i++) topH += heightOf(i);
    let bottomH = 0;
    for (let i = end; i < count; i++) bottomH += heightOf(i);

    return { start, end, topH, bottomH };
}

// The bare visible band — the rows a reader can actually SEE, with no overscan
// padding. Distinct from `computeWindow`'s range, which is deliberately wider
// (and collapses to the whole list when the viewport is unmeasured), so a
// position readout built on the window over-reports by up to 2x overscan and
// silently degrades to "everything" exactly when the geometry is unknown.
// `measured` says whether the answer means anything: false when the viewport
// height is unknown or the list is empty, where "what is visible" has no
// answer and a caller must not present the band as fact.
export function visibleBand(metrics) {
    const { viewportH, count } = metrics;
    if (viewportH <= 0 || count === 0) {
        return { start: 0, end: count, measured: false };
    }
    const { start, end } = scanBand(metrics); // no padding → bare visible band
    return { start, end, measured: true };
}

// Value equality for two visible bands, so a caller can skip a state update
// when the band didn't move.
export function bandsEqual(a, b) {
    return a.start === b.start && a.end === b.end && a.measured === b.measured;
}

// Hysteresis wrapper. Returns `prev` (same reference) when the freshly visible
// band still sits inside the previously-rendered [start, end) — so the caller
// can `if (next === prev) return;` and skip a re-render. Recomputes (fresh
// overscan + spacers) the moment the band escapes the buffer, the list resizes,
// or there is no usable previous window.
//
// NOTE: this short-circuits on *scroll* only. When measured heights change
// (rows mount/wrap), call `computeWindow` directly instead — the spacer heights
// must be rebuilt even if the index range is unchanged.
export function reconcileWindow(prev, metrics) {
    const { viewportH, count } = metrics;
    if (viewportH <= 0 || count === 0) return computeWindow(metrics);

    if (prev && prev.end > prev.start && prev.start >= 0 && prev.end <= count) {
        const visible = scanBand(metrics); // no padding → bare visible band
        if (visible.start >= prev.start && visible.end <= prev.end) {
            return prev;
        }
    }
    return computeWindow(metrics);
}

// A row scrolled almost entirely out of frame shouldn't count as the one the
// reader is looking at, so a row wins only once its bottom edge clears the
// viewport's top by more than this.
export const TOP_VISIBLE_SLACK_PX = 24;

// The first still-visible row at the top of a scroll viewport — used to carry
// the reader's position across a view pivot (trace detail's Context ⇄ X-ray
// toggle). Rows are `{ bottom, index }` pairs in viewport coordinates, so the
// caller keeps the DOM read and this stays pure arithmetic (jsdom reports every
// rect as zero, which is exactly why the geometry can't live in the component).
// Rows are expected in document order; a row with an unusable index is skipped
// rather than ending the scan.
export function topVisibleIndex(rows, viewportTop = 0) {
    for (const r of rows || []) {
        if (!r || !Number.isFinite(r.bottom) || !Number.isFinite(r.index)) continue;
        if (r.bottom > viewportTop + TOP_VISIBLE_SLACK_PX) return r.index;
    }
    return null;
}

// Dev-only nudge when a virtualizer is forced to key a row/item by array index
// because no stable id was available. Index keys are the silent cause of
// height-cache drift and scroll jumps when a list reorders or filters — the
// exact failure mode @pierre/trees designs out by keying everything on path.
// Warns once per call site so it surfaces the gap without spamming.
const warnedKeyFallbacks = new Set();
export function warnIndexKeyFallback(where) {
    if (warnedKeyFallbacks.has(where)) return;
    warnedKeyFallbacks.add(where);
    const isDev =
        typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV;
    if (isDev && typeof console !== "undefined") {
        console.warn(
            `[virtualWindow] ${where}: a row/item has no stable id; falling back to ` +
                `array index as its key. Index keys drift the height cache and cause ` +
                `scroll jumps when the list reorders or filters. Pass getRowId / ` +
                `getItemKey (or give items an \`id\`).`,
        );
    }
}
